import { Fragment, useContext, useEffect, useState } from "react";
import { LayerInfo } from "src/common/managers/Types";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { SheetFunc } from "src/managers/SheetFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ExportData } from "src/managers/ExportData";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { GlassButton } from "./GlassButtons";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput";
import { ManagedWindow } from "src/WindowManager/WindowState";

type exportType = "xlsx" | "geojson" | "layerInfo" | "styleJson" | "shp" | "kml" | "gml" | "gpkg" | "dxf" | "dgn"
type callbackEvent = "cancel" | "accept"


type Props = {
  managedWindow: ManagedWindow;
  layers?: LayerInfo | LayerInfo[]
  callBack: (evt: callbackEvent) => void
};

function layerHasGeoJsonFeatureTable(layer: LayerInfo): boolean {
  return Array.isArray(layer.geoJson?.features);
}

function defaultType(layer: LayerInfo): exportType {
  if (layer.type.includes("Point")) {
    return "xlsx"
  }
  return "geojson"
}

function availableTypes(layer: LayerInfo, debugFeature?: boolean): exportType[] {
  let types = [
    ...(layerHasGeoJsonFeatureTable(layer) || layer.type.includes("Point") ? ["xlsx"] : []),
    "shp",
    "kml",
    "gml",
    "gpkg",
    "geojson",
    "styleJson",
    ...(debugFeature ? ["layerInfo"] : []),
  ]
  return types as exportType[]
}

export function DownloadDialog(props: Props) {
  const { hasAccessToFeature } = useContext(ApplicationStateContext)
  const layers = !props.layers ? [] : "type" in props.layers ? [props.layers] : props.layers
  const [name, setName] = useState(layers.map((a) => a.datasetname))
  const [format, setFormat] = useState(layers.map((a) => defaultType(a)))

  useEffect(() => {
    setName(layers.map((a) => a.datasetname))
    setFormat(layers.map((a) => defaultType(a)))
  }, [props.layers])

  function updateName(value: string, idx: number) {
    setName((name) => name.map((a, idy) => idx === idy ? (value ?? "") : a))
  }

  function Accept() {
    layers.map((a, idx) => {
      const exportlayer: (LayerInfo & { export: exportType }) = { ...a, datasetname: name[idx], export: format[idx] }
      downloadSingleLayer(exportlayer)
    })
    props.callBack("accept")
  }

  function Cancel() {
    props.callBack("cancel")
  }


  return <AdvancedDragModal
    window={props.managedWindow}
    title={Localization.getText("Export")}
    variant="Info"
  >
    <ADModalBody>
      <GlassInputGroup
        autoFocus={true}
        onEscape={Cancel}
        onSubmit={Accept}
      >
        {layers.map((a, idx) => {
          const exportTypes = availableTypes(a, hasAccessToFeature(Feature.Debugging))

          return (
            <Fragment key={a.layerId ?? "Error"}>

              <GlassTextInputTransformer
                label={Localization.getText("Layer") + ": " + (idx + 1)}
              >
                <GlassInputControl>
                  <input type="text" value={name[idx]} placeholder={a.datasetname ?? "Error"} onChange={(ev) => updateName(ev.target.value, idx)} />
                  <select value={format[idx]} style={{ flex: "unset", width: "18ex", cursor: "pointer" }}
                    onChange={((a) => setFormat((sf) => sf.map((cur, idy) => idx === idy ? a.target.value as exportType || "geojson" : cur)))}
                  >
                    {exportTypes.map((a) => {
                      return (
                        <option key={a} value={a}>.{a}</option>
                      )
                    })}
                  </select>

                  {/* <div onClick={() => changeFormat(idx)}>{"."+format[idx]}</div> */}
                </GlassInputControl>
              </GlassTextInputTransformer>
            </Fragment>)
        })}
      </GlassInputGroup>
    </ADModalBody>
    <ADModalFooter>
      <GlassButton onClick={Accept}>{Localization.getText("Export")}</GlassButton>
      <GlassButton onClick={Cancel}>{Localization.getText("Cancel")}</GlassButton>
    </ADModalFooter>
  </AdvancedDragModal>
}


function downloadSingleLayer(layerInfo: (LayerInfo & { export: exportType })) {
  let blob: Blob | null = null
  let extension = ".xlsx"

  switch (layerInfo.export) {
    case "layerInfo":
      blob = new Blob([JSON.stringify(layerInfo)], { type: "application/json" })
      extension = ".layerinfo"
      break;
    case "styleJson":
      blob = new Blob([JSON.stringify({
        ...validGeoJson(layerInfo.geoJson)
        , "style": layerInfo.styling
      })], { type: "application/json" })
      extension = ".geojson"
      break;
    case "geojson":
      blob = new Blob([JSON.stringify(validGeoJson(layerInfo.geoJson))], { type: "application/json" })
      extension = ".geojson"
      break;
    case "shp":
    case "kml":
    case "gml":
    case "gpkg":
    case "dxf":
    case "dgn":
      blob = new Blob([JSON.stringify(validGeoJson(layerInfo.geoJson))], { type: "application/json" })
      let formData = new FormData();
      let transformBaseUrl = SettingsManager.getSystemSetting("transformBaseUrl");
      formData.append("input", blob, (layerInfo.datasetname ?? "MapitDownload") + ".geojson")
      fetch(transformBaseUrl + "/" + layerInfo.export, {
        method: "POST",
        body: formData
      }).then(async (response) => {
        if (response.ok) {
          await response.json().then((data) => {
            if ("files" in data) {
              for (const file of data.files) {
                fetch(transformBaseUrl + "/" + file).then(async (response) => {
                  if (response.ok) {
                    await response.blob().then((blob) => {
                      ExportData.downloadBlob(file.split("/").pop() ?? "MapitDownload", blob)
                    })
                  }
                })
              }
            }
          })
        }
      })
      return;
    case "xlsx": {
      const features = layerInfo.geoJson?.features ?? [];
      let sheet = SheetFunc.jsonToSheet(features.map((a) => geoJsonFeatureToExcelRow(a)));
      blob = SheetFunc.createBlob(SheetFunc.sheetToDownload(sheet));
      break;
    }
  }
  ExportData.downloadBlob((layerInfo.datasetname ?? "MapitDownload") + extension, blob)
}

/** One spreadsheet row per feature: attribute columns only (no WKT — use GeoJSON export for geometry). */
function geoJsonFeatureToExcelRow(feature: { geometry?: { type?: string; coordinates?: number[] }; properties?: Record<string, unknown> }) {
  const properties = omitGeometryPropertyKeys(feature.properties ?? {});
  if (!feature.geometry?.type) {
    return properties;
  }
  if (feature.geometry.type === "Point") {
    const coords = feature.geometry.coordinates ?? [];
    return { ...properties, "Lat": coords[0], "Lng": coords[1] };
  }
  return properties;
}

const geometryPropertyKeys = new Set(["wkt", "geometri", "geometry", "geoshape"]);

function omitGeometryPropertyKeys(properties: Record<string, unknown>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!geometryPropertyKeys.has(key.toLowerCase())) {
      row[key] = value;
    }
  }
  return row;
}

// Currently only converts coords to numbers
function validGeoJson(geojson: any) {
  return GenerateGeomUtils.geoJsonCoordsTransformer(geojson, (coord) => {
    return coord.map((a) => Number(a)) as [number, number]
  })
}